import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { LiksPage } from '../../components/liks/LiksPage/LiksPage';
import { LiksPageHeading } from '../../components/liks/LiksPageHeading/LiksPageHeading';
import { useLacTenantsQuery } from '../../features/lac/queries/useLacTenantsQuery';
import { useLacUserQuery } from '../../features/lac/queries/useLacUserQuery';
import { useI18n } from '../../i18n/useI18n';
import './LacUserProfilePage.css';
import { LacUserProfileAdminBar } from './LacUserProfileAdminBar';
import { LacUserProfileForm } from './LacUserProfileForm';
import {
  resolveLacUserProfilePath,
  resolveLacTenantPath,
  resolveLacTabPath,
} from '../../routes/lacPaths';
import { resolveUserRef } from '../../features/user/userDisplay';

export function LacUserProfilePage() {
  const { translate } = useI18n();
  const { tenantName, userRef } = useParams<{
    tenantName: string;
    userRef: string;
  }>();

  const userQuery = useLacUserQuery(tenantName, userRef);
  const tenantsQuery = useLacTenantsQuery();
  const profile = userQuery.data;
  const tenants = useMemo(() => tenantsQuery.data ?? [], [tenantsQuery.data]);

  const tenantDisplayName = useMemo(() => {
    if (!tenantName) {
      return '';
    }
    const tenant = tenants.find((item) => item.name === tenantName);
    return tenant?.displayName?.trim() || tenantName;
  }, [tenantName, tenants]);

  if (!tenantName || !userRef) {
    return <Navigate to={resolveLacTabPath()} replace />;
  }

  if (userQuery.isLoading) {
    return (
      <LiksPage
        size="big"
        content={
          <p className="lac-user-profile__status">
            {translate('lac.users.profile.loading')}
          </p>
        }
      />
    );
  }

  if (userQuery.isError || !profile) {
    return (
      <LiksPage
        size="big"
        header={
          <LiksPageHeading
            aria-label={translate('lac.users.profile.breadcrumbLegend')}
            items={[
              { label: translate('lac.title'), to: resolveLacTabPath() },
              {
                label: tenantDisplayName || tenantName,
                to: resolveLacTenantPath(tenantName, 'users'),
              },
              { label: translate('lac.users.profile.title') },
            ]}
          />
        }
        content={
          <p className="lac-user-profile__status" role="alert">
            {translate('lac.users.profile.loadError')}
          </p>
        }
      />
    );
  }

  const profileUserRef = resolveUserRef(profile);
  if (profileUserRef !== '' && profileUserRef !== userRef) {
    return (
      <Navigate
        to={resolveLacUserProfilePath(tenantName, profileUserRef)}
        replace
      />
    );
  }

  const headingName =
    [profile.firstName, profile.lastName]
      .filter((part): part is string => Boolean(part?.trim()))
      .join(' ') || profile.name;

  return (
    <LiksPage
      size="big"
      className="lac-user-profile"
      header={
        <LiksPageHeading
          aria-label={translate('lac.users.profile.breadcrumbLegend')}
          items={[
            { label: translate('lac.title'), to: resolveLacTabPath() },
            {
              label: tenantDisplayName,
              to: resolveLacTenantPath(tenantName, 'users'),
            },
            { label: headingName },
          ]}
        />
      }
      content={
        <div className="lac-user-profile__content">
          <LacUserProfileAdminBar
            key={`${tenantName}:${userRef}:admin`}
            profile={profile}
            tenantName={tenantName}
            userRef={userRef}
          />
          <LacUserProfileForm
            key={`${tenantName}:${userRef}`}
            profile={profile}
            tenantName={tenantName}
            userRef={userRef}
            tenantDisplayName={tenantDisplayName}
          />
        </div>
      }
    />
  );
}
